import { ErrorDebugAddonKeys, getGlobalAP, isObject, FetchConnectError } from '../../../../common';
import { handleConnectError } from '../../../../common/fetch-connect-error';
import type { ACJSHost, ExtensionParams, ErrorAttributes } from '../../../../types';
import { Deferred } from './deferred';
import type { DeferredConstructor, DeferredType } from './deferred.types';
import type { ConnectIframeEvent, ResolveData } from './types';
import { pick } from './utils';

/**
 * Strip off the initial slash added by URL.
 */
const sanitizeModuleKey = (moduleKey: string) =>
	new URL(moduleKey, window.location.origin).pathname.slice(1);

const getContentUrl = (pluginKey: string, moduleKey: string) =>
	`/plugins/servlet/ac/${encodeURIComponent(pluginKey)}/${encodeURIComponent(
		sanitizeModuleKey(moduleKey),
	)}`;

const appendPrefixToCustomParameters = (
	params: Record<string, unknown>,
): Record<string, unknown> => {
	const newObj: Record<string, unknown> = {};
	Object.keys(params).forEach((key) => {
		newObj[`ac.${key}`] = params[key];
	});

	return newObj;
};

/**
 * Calculate differences in client / server time.
 * Set the clockSkew to the difference + 1 minute of skew.
 */
const updateClockSkew = (date: string) => {
	const parsedServerDateTime = Math.round(Date.parse(date) / 1000);
	const now = Math.round(Date.now() / 1000);
	let skew: number;

	if (!Number.isNaN(parsedServerDateTime)) {
		skew = Math.abs(now - parsedServerDateTime);
		window.connectHost.setJwtClockSkew(skew + 60);
	}
};

const cacheableLookup = <T extends ExtensionParams>(params: T): string | undefined => {
	const key = `${params.addon_key}__${params.key}`;

	const AP = getGlobalAP();
	if (AP && AP.cacheableIframeUrls && AP.cacheableIframeUrls[key]) {
		return AP.cacheableIframeUrls[key];
	}

	return undefined;
};

const fetchData = (
	contentResolverUrl: string,
	data: ResolveData,
	params: ExtensionParams,
	parametersToSendToServer: Record<string, unknown>,
) => {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const dataObj = {
		...data,
		...appendPrefixToCustomParameters(parametersToSendToServer),
	} as Record<string, string>;

	const handError = (error: FetchConnectError, extraAttrs: ErrorAttributes = {}) => {
		handleConnectError('fetchModuleConfiguration', error, {
			extId: params?.id,
			extKey: params?.key,
			addonKey: params?.addon_key,
			moduleKey: params?.module_key,
			...extraAttrs,
		});
	};

	const fetchOptions = {
		method: 'POST',
		body: new URLSearchParams(dataObj),
		headers: {
			'Content-Type':
				params.classifier === 'json' ? 'application/x-www-form-urlencoded' : 'text/html',
		},
	};

	const defaultFetchErrorMsg =
		'Unable to retrieve addon module URL. Please check your specified module key.';

	return fetch(contentResolverUrl, fetchOptions)
		.then((response) => {
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			updateClockSkew(response.headers.get('Date')!);

			const statusCode = response.status;
			const connectError = new FetchConnectError('Network request failed', statusCode);
			const errorAttrs = {
				errorText: defaultFetchErrorMsg,
			};

			if (!response.ok) {
				// try to parse body to text
				response
					.text()
					.catch(() => '')
					.then(() => {
						handError(connectError, {
							...errorAttrs,
						});
					});

				return Promise.reject(Object.assign(connectError, errorAttrs));
			}

			return response.json();
		})
		.then((responseJson) => {
			if (
				params.addon_key &&
				ErrorDebugAddonKeys.indexOf(params.addon_key) !== -1 &&
				responseJson.status === 403
			) {
				const statusCode = responseJson.status;
				const connectError = new FetchConnectError(responseJson.message, statusCode);

				handError(connectError);
			}

			return responseJson;
		})
		.then((responseJson) => getGlobalAP()._convertConnectOptions(responseJson))
		.catch((error) => {
			const errorText = error.message || error.statusText || defaultFetchErrorMsg;
			const responseText =
				typeof error.responseText === 'string'
					? error.responseText.substring(0, 1000)
					: 'Something unexpected went wrong with the request';
			const httpStatusCode = error.status;

			if (window.connectHost && window.connectHost.trackContentResolverEvent && error) {
				window.connectHost.trackContentResolverEvent(false, {
					extension: params,
					attrs: {
						contentResolverUrl,
						httpStatusCode,
						errorText,
						responseText,
					},
				});
			}

			const connectError = new FetchConnectError(errorText, httpStatusCode, error);
			handError(connectError);

			// eslint-disable-next-line prefer-promise-reject-errors
			return Promise.reject({
				text: defaultFetchErrorMsg,
				cause: connectError,
			});
		});
};

export const resolveByExtension = (
	params: ExtensionParams,
	noExtensionCreate: boolean,
): DeferredType<ExtensionParams> => {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const defer = new (Deferred as unknown as DeferredConstructor<ExtensionParams>)();
	const cacheResp = cacheableLookup(params);
	// if it's in the cache and we're trying to create an extension
	if (cacheResp && !noExtensionCreate) {
		defer.resolve(
			getGlobalAP()._convertConnectOptions({
				addon_key: params.addon_key,
				key: params.key,
				url: cacheResp,
				productCtx: JSON.stringify(params.options.productContext || {}),
			}),
		);
		return defer;
	}

	const data: ResolveData = {
		'plugin-key': params.addon_key,
		'product-context': JSON.stringify(params.options.productContext || {}),
		key: params.key,
		width: params.width || '100%',
		height: params.height || '100%',
		classifier: params.classifier || 'raw',
	};
	if (params.options.contentClassifier) {
		data.classifier = [params.options.contentClassifier, data.classifier];
	}
	const customData = params.options.customData || {};
	const parametersToSendToServer = pick(customData, (_key, value) => {
		if (isObject(value)) {
			return false;
		}
		if (typeof value === 'string' || value instanceof String) {
			return value.length < 255;
		}
		return true;
	});

	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	const contentResolverUrl = getContentUrl(params.addon_key!, params.key!);

	fetchData(contentResolverUrl, data, params, parametersToSendToServer)
		.then((d) => {
			defer.resolve(d);
		})
		.catch((error) => {
			defer.reject(error);
		});

	return defer;
};

export const getIframeUrlSetter = (connectHost: ACJSHost) =>
	function setInnerIframeUrl(event: ConnectIframeEvent) {
		if (event.data.type === 'set_inner_iframe_url') {
			const { iframeData } = event.data;
			iframeData.classifier = 'json';
			return resolveByExtension(event.data.iframeData, true).then((data) => {
				// eslint-disable-next-line no-param-reassign
				data.id = event.data.extension_id;
				connectHost.registerExistingExtension(data.id, { extension: data });
				// eslint-disable-next-line no-param-reassign
				event.source.location = data.url;
			});
		}
		return undefined;
	};

export const registerContentResolver = (connectHost: ACJSHost) => {
	connectHost.registerContentResolver.resolveByExtension(resolveByExtension);
	// @ts-expect-error TS2769: No overload matches this call
	window.addEventListener('message', getIframeUrlSetter(connectHost));
};
